import { css, cx } from '@emotion/css';
import { useImperativeEffect } from '@snapchat/core-browser';
import { Brand } from '@snapchat/mw-common';
import { useContentfulImages } from '@snapchat/mw-contentful-client';
import type { Banner as BannerType, PageBlocksItem } from '@snapchat/mw-contentful-schema';
// TODO: Move this to common/client.
import type { BackgroundColor, ImageSources } from '@snapchat/snap-design-system-marketing';
import {
  AiLocalizationBanner,
  Banner,
  bannerMinHeight,
  BreakOverlayType,
  Break as BreakSDS,
  BreakSpacer as BreakSpacerSDS,
  FootnoteBlock,
  footnoteBlockBackgroundColor,
  getBackgroundClassName,
  getBreakOverlayType,
  Header as HeaderSDS,
  MessageContext,
  MotifScheme,
  Page as PageSDS,
  PictureContext,
  Template,
  useIsMobile,
} from '@snapchat/snap-design-system-marketing';
import type { CSSProperties, FC, ReactElement, ReactNode } from 'react';
import { cloneElement, Fragment, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { AppContext } from '../../AppContext';
import { Config } from '../../config';
import { FooterView, HeaderView, PageLayoutContext } from '../../context/PageLayoutContext';
import { SubscribedEventType } from '../../helpers/logging/eventListenerTypes';
import { logEvent } from '../../helpers/logging/loggingInstance';
import { useAlternateBrandPropOverride } from '../../hooks/useAlternateBrandPropOverride';
import { useBrand } from '../../hooks/useBrand';
import { useMotifScheme } from '../../hooks/useMotifScheme';
import { useParseMediaContainer } from '../../hooks/useParseMediaContainer';
import type { RichText } from '../../types/RichText';
import { combineImageSources } from '../../utils/combineImageSources';
import { getContentfulInspectorProps } from '../../utils/contentful/getContentfulInspectorProps';
import { direction } from '../../utils/direction';
import { renderRichTextWithElements } from '../../utils/renderText/renderRichText';
import { AlternateLang } from '../AlternateLang/AlternateLang';
import type { BlockDataProps } from '../Block';
import { Block } from '../Block';
import { BlockTabs } from '../BlockTabs';
import type { BreakBrandColor } from '../Break';
import { Canonical } from '../Canonical/Canonical';
import { getCustomComponents } from '../customComponents';
import { DefinitionOverlay, DefinitionProvider } from '../Definition';
import { Feature, useFeatureFlags } from '../FeatureFlags';
import { Footnote } from '../Footnote';
import { totalHeaderHeightCssVar } from '../Header/headerSizeUtils';
import { Hero } from '../Hero';
import { LatestPostHero } from '../Hero/LatestPostHero';
import { HlsInitializer } from '../HlsInitializer/HlsInitializer';
import { getImageSourceSettings } from '../Image';
import { ImmersiveScrollBlock } from '../ImmersiveScrollBlock';
import { LatestPostBlock } from '../LatestPostBlock';
import { Metas } from '../Metas';
import { MultiValuePropBlock } from '../MultiValuePropBlock';
import { MultiVideoBlock } from '../MultiVideoBlock';
import { PersistentCta } from '../PersistentCta/PersistentCta';
import { ScrollAnimatedSection } from '../ScrollAnimatedSection';
import { minHeightCss } from '../Slug/Slug.styles';
import { SlugContext } from '../Slug/SlugContext';
import { SplitBlock } from '../SplitBlock/SplitBlock';
import { SubNavigation } from '../SubNavigation';
import { ActiveEventCountTracker } from '../tracing/ActiveEventCountTracker';
import { WebSchemas } from '../WebSchema';
import {
  bannerContentContainerCss,
  pageContainerCss,
  pageContainerWithSideNavCss,
  pageStickyCss,
  stickyBottomCss,
} from './Page.styles';
import { PageBottomStickyPortal } from './PageBottomStickyPortal';
import type { PageDataBlock, PageDataBlockWithBackgroundColor } from './pageQuery';
import { pageStickyHeightCssVar } from './pageStickyUtils';
import type { PageDataProps } from './types';

const defaultBreakTemplates: Template[] = [
  Template.Straight,
  Template.Skirt,
  Template.Straight,
  Template.HeadFlipped,
  Template.Straight,
  Template.Head,
];

const pageCss = css`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - var(${totalHeaderHeightCssVar}));
`;

const pageBottomFixedElementsCss = css`
  display: flex;
  flex-direction: column;
  margin-top: auto;
`;

// TODO: Use a variable from SDS-M when it exposes this as a const.
const subNavHeightPx = 60;

const pageWithSubNavCss = css`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - calc(var(${totalHeaderHeightCssVar}) + ${subNavHeightPx}px));
`;

// We declare these as constants so they are the same object across renders to prevent
// excessive rerendering of things that rely on the context.
const pictureContextLazy = { lazy: true };
const pictureContextNoLazy = { lazy: false };

type BlockType = PageBlocksItem['__typename'] | 'Unknown';

const allBlockTypes: BlockType[] = [
  'Break',
  'SubNavigation',
  'BlockHero',
  'LatestPosts',
  'MultiVideoBlock',
  'Block',
  'SplitBlock',
  'MultiValuePropBlock',
  'BlockTabs',
];

const isCustomComponent = (type: BlockType) => !allBlockTypes.includes(type);

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO: Refactor to reduce complexity.
export const Page: FC<PageDataProps> = props => {
  const { pathname } = useLocation();
  const featureFlags = useFeatureFlags();
  const { formatMessage } = useContext(MessageContext);
  const { currentLocale } = useContext(AppContext);
  const { getImageSources } = useContentfulImages();
  const isMobile = useIsMobile();

  const brand = useBrand();
  const isSnapBrand = brand === Brand.SNAP;

  const pageMotifScheme = useMotifScheme(props.brandBackgroundColor);
  const bannerMotifScheme = useMotifScheme(
    props.banner?.__typename === 'Banner'
      ? (props.banner?.brandBackgroundColor as MotifScheme)
      : undefined
  );

  // This is only used in sandbox to override the break templates and hero break template props for other brands
  const alternateBrandPropOverride = useAlternateBrandPropOverride(Config, 'page');

  const stickyContainerRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);

  // Unfortunately, we have to do this check here because on forbusiness, we use the same banner slot
  // to render the mobile CTA, but the mobile CTA doesn't use the sticky container
  // TODO: Rethink how this is done. Current this is done here because SSR needs to have a value... but does it really?
  const stickyHeightRef = useRef<number>(
    props.banner && props.banner.__typename === 'Banner' ? bannerMinHeight : 0
  );

  // TODO: Extract as a hook.
  const resizeObserver = useMemo(() => {
    // attach resize observer on client
    if (typeof window !== 'undefined') {
      return new ResizeObserver(entries => {
        const { height } = entries[0]!.contentRect;

        if (stickyHeightRef.current !== height) {
          stickyHeightRef.current = height;

          pageRef.current?.style.setProperty(pageStickyHeightCssVar, `${height}px`);
        }
      });
    }

    return undefined;
    // we disable here because we only need it to run once on mount (on ssr and on client)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (stickyContainerRef.current && resizeObserver) {
      resizeObserver.observe(stickyContainerRef.current, { box: 'border-box' });

      return () => {
        resizeObserver.disconnect();
      };
    }

    return;
  }, [resizeObserver]);

  useImperativeEffect(() => {
    // Note we log the page load event here, because at this point the page experiment information is available.
    logEvent({
      subscribedEventType: SubscribedEventType.PAGE_LOAD,
    });
  }, [pathname]);

  // NOTE: Since these useEffect calls can modify renders above this component,
  // we need to ensure they are always run (and not gated by an if statement).
  const {
    headerView: currentHeaderView,
    footerView: currentFooterView,
    hasSideNav,
    hasSubNav,
    setHeaderView,
    setFooterView,
  } = useContext(PageLayoutContext);
  const headerView = props.headerView ?? HeaderView.FULL_HEADER;
  const footerView = props.footerView ?? FooterView.FULL_FOOTER;

  // Hack to mark the session header/footer with the correct values on the
  // server-side only. Since useEffect doesn't run on the server, we need to
  // set the value somehow.
  // One of the issues here is that this only works if there are async promises
  // in the first render pass that forces the server re-render everything. In
  // those cases the server will pick up the correct values on the second pass.
  // So, if for some reason, the server hasn't re-rendered (because all async
  // data is already in the cache), then the server will have the wrong values.
  if (!Config.isClient) {
    setHeaderView?.(headerView);
    setFooterView?.(footerView);
  }

  useEffect(() => {
    if (headerView === currentHeaderView) return;
    setHeaderView?.(headerView);
  }, [headerView, currentHeaderView, setHeaderView]);

  useEffect(() => {
    if (footerView === currentFooterView) return;
    setFooterView?.(footerView);
  }, [footerView, currentFooterView, setFooterView]);

  const slugContext = useContext(SlugContext);

  const {
    title,
    metas,
    blocksCollection,
    footnotesCollection,
    collapseFootnotes,
    backgroundColor,
    backgroundMediaV2,
    backgroundMediaStyle,
    scrollSnap,
    mobileCta,
    showAiLocalizationBanner = [],
    denseLayout,
  } = props;

  // This value is loaded here for testing.
  // TODO: Add regression tests to very this value is set correctly.
  const testFeature = featureFlags[Feature.TEST_FEATURE];

  const pageBackgroundColor = backgroundColor ?? Config.theme?.defaultPageBackgroundColor;

  // =================================================================================================
  // Footnotes
  // =================================================================================================
  // TODO: This should probably be OL and children should be LI
  const footnotes = footnotesCollection?.items?.map(footnoteProps => (
    <Footnote key={footnoteProps.sys.id} {...footnoteProps} />
  ));

  const footnotesTitle = formatMessage({ id: 'footnotesTitle', defaultMessage: '' });

  // Don't pass in a footnoteBlock if there are no footnotes
  const footnoteBlock = footnotes?.length ? (
    <FootnoteBlock title={footnotesTitle} isOpenInitially={!collapseFootnotes}>
      {footnotes}
    </FootnoteBlock>
  ) : undefined;

  // =================================================================================================
  // AI Localization Banner
  // =================================================================================================

  const aiLocalizationBannerTitle = formatMessage({
    id: 'aiLocalizationBannerTitle',
    defaultMessage: '',
  });
  const aiLocalizationBannerDescription = formatMessage({
    id: 'aiLocalizationBannerDescription',
    defaultMessage: '',
  });

  const aiLocalizationBanner = showAiLocalizationBanner?.includes(currentLocale) ? (
    <AiLocalizationBanner
      title={aiLocalizationBannerTitle}
      description={aiLocalizationBannerDescription}
    />
  ) : undefined;

  // =================================================================================================
  // All blocks from CMS
  // =================================================================================================
  // TODO: This should be using PageBlocksItem
  const blocks: PageDataBlock[] = [];
  blocks.push(...(blocksCollection?.items ?? []));

  // =================================================================================================
  // Convert blocks data to components and auto-break insertion
  // =================================================================================================
  const breakTemplates =
    alternateBrandPropOverride?.breakTemplates ??
    Config.theme?.breakTemplates ??
    defaultBreakTemplates;
  let templateIndex = 0;
  let firstHeroOrBlockIndex = -1;

  const blocksWithBreaks: { data: PageDataBlock; component: ReactElement }[] = [];

  blocks.forEach((props, idx) => {
    const { sys, __typename } = props;

    const isLastBlock = idx === blocks.length - 1;

    const prevBlock = blocks?.[idx - 1];
    const nextBlock = blocks?.[idx + 1];

    const blockBackgroundColor = (props as PageDataBlockWithBackgroundColor)?.backgroundColor;
    const prevBackgroundColor = (prevBlock as PageDataBlockWithBackgroundColor)?.backgroundColor;
    const nextBackgroundColor = (nextBlock as PageDataBlockWithBackgroundColor)?.backgroundColor;

    const blockBrandBackgroundColor = (props as PageDataBlockWithBackgroundColor)
      ?.brandBackgroundColor;
    const prevBrandBackgroundColor = (prevBlock as PageDataBlockWithBackgroundColor)
      ?.brandBackgroundColor;
    const nextBrandBackgroundColor = (nextBlock as PageDataBlockWithBackgroundColor)
      ?.brandBackgroundColor;

    const blockHasBackgroundMedia = !!(props as BlockDataProps)?.backgroundMediaV2;
    const prevBlockHasBackgroundMedia = !!(prevBlock as BlockDataProps)?.backgroundMediaV2;
    const nextBlockHasBackgroundMedia = !!(nextBlock as BlockDataProps)?.backgroundMediaV2;

    const blockEffectiveColor =
      blockHasBackgroundMedia || (!blockBrandBackgroundColor && !blockBackgroundColor)
        ? undefined
        : blockBrandBackgroundColor ??
          (getBackgroundClassName(blockBackgroundColor) as MotifScheme);

    const nextEffectiveColor =
      nextBlockHasBackgroundMedia || (!nextBrandBackgroundColor && !nextBackgroundColor)
        ? undefined
        : nextBrandBackgroundColor ?? (getBackgroundClassName(nextBackgroundColor) as MotifScheme);

    // get index of first hero or block (basically anything thats not subnav)
    // to guestimate above the fold
    if (firstHeroOrBlockIndex === -1 && __typename !== 'SubNavigation') {
      firstHeroOrBlockIndex = idx;
    }

    // Create component based on block type
    switch (__typename) {
      case 'Break': {
        // Use the newer 'brandType' property if it exists, otherwise use the older 'type' property
        const breakType = props.brandType ?? props.type;

        // TODO: Extract this out into a mwp/components/Break.
        if (breakType && breakType !== 'None') {
          // Convert legacy BreakBackgroundColor to BreakBrandColor (MotifScheme)
          const deprecatedTopColorAsBrand: BreakBrandColor | undefined = isSnapBrand
            ? undefined
            : props.topColor
            ? props.topColor === 'Transparent'
              ? 'Transparent'
              : (getBackgroundClassName(props.topColor) as MotifScheme)
            : undefined;

          const deprecatedBottomColorAsBrand: BreakBrandColor | undefined = isSnapBrand
            ? undefined
            : props.bottomColor
            ? props.bottomColor === 'Transparent'
              ? 'Transparent'
              : (getBackgroundClassName(props.bottomColor) as MotifScheme)
            : undefined;

          // Auto-detect from adjacent blocks: brandBackgroundColor, or convert
          // backgroundColor to MotifScheme via getBackgroundClassName
          const topColorFallback: BreakBrandColor =
            prevBlockHasBackgroundMedia || (!prevBrandBackgroundColor && !prevBackgroundColor)
              ? 'Transparent'
              : prevBrandBackgroundColor ??
                (getBackgroundClassName(prevBackgroundColor) as MotifScheme);

          let bottomColorFallback: BreakBrandColor;

          if (isLastBlock && !!footnoteBlock) {
            bottomColorFallback = getBackgroundClassName(
              footnoteBlockBackgroundColor
            ) as MotifScheme;
          } else {
            bottomColorFallback =
              nextBlockHasBackgroundMedia || (!nextBrandBackgroundColor && !nextBackgroundColor)
                ? 'Transparent'
                : nextBrandBackgroundColor ??
                  (getBackgroundClassName(nextBackgroundColor) as MotifScheme);
          }

          // We need to default to the default motif scheme for Snap brand for a smoother transition from snapchat brand
          // Priority: brandTopColor > topColor > prev block brandBackgroundColor > prev block backgroundColor
          const topColor =
            (isSnapBrand ? props.brandTopColor || MotifScheme.DEFAULT : props.brandTopColor) ??
            deprecatedTopColorAsBrand ??
            topColorFallback;
          const bottomColor =
            (isSnapBrand
              ? props.brandBottomColor || MotifScheme.DEFAULT
              : props.brandBottomColor) ??
            deprecatedBottomColorAsBrand ??
            bottomColorFallback;

          const topColorClean = topColor === 'Transparent' ? undefined : (topColor as MotifScheme);
          const bottomColorClean =
            bottomColor === 'Transparent' ? undefined : (bottomColor as MotifScheme);

          const { desktopSettings, mobileSettings } = getImageSourceSettings({
            desktopHeight: props.media?.media?.height ?? 0,
            mobileHeight: props.media?.mobileMedia?.height ?? 0,
            enableHighDpi: props.media?.enableHighDpi,
            quality: props.media?.quality,
          });

          const imgSrcs = combineImageSources({
            desktop: getImageSources(props.media?.media.url, desktopSettings),
            mobile: getImageSources(props.media?.mobileMedia?.url, mobileSettings),
          });

          blocksWithBreaks.push({
            data: {
              ...props,
              brandTopColor: topColor,
              brandBottomColor: bottomColor,
            },
            component: (
              <BreakSDS
                key={props.sys.id}
                template={Template[breakType]}
                imgSrcs={imgSrcs}
                topColor={topColorClean}
                bottomColor={bottomColorClean}
                isOverlaid={props.isOverlaid && !isLastBlock}
              />
            ),
          });
        }
        break;
      }

      case 'ScrollAnimatedSection': {
        if (isMobile) {
          const mobileContent = props.mobileContentCollection?.items ?? [];

          for (const blockProps of mobileContent) {
            const blockPropsAsDataProps = blockProps as BlockDataProps;

            blocksWithBreaks.push({
              data: props,
              component: (
                <Block
                  key={sys.id}
                  {...blockPropsAsDataProps}
                  isPreviousSameBackgroundColor={
                    blockPropsAsDataProps.backgroundColor === prevBackgroundColor
                  }
                  isNextSameBackgroundColor={
                    blockPropsAsDataProps.backgroundColor === nextBackgroundColor
                  }
                  fullHeight={scrollSnap || blockPropsAsDataProps.fullHeight}
                />
              ),
            });
          }
          break;
        }

        blocksWithBreaks.push({
          data: props,
          component: <ScrollAnimatedSection key={sys.id} {...props} />,
        });
        break;
      }

      case 'SubNavigation': {
        blocksWithBreaks.push({
          data: props,
          component: (
            <SubNavigation
              key={sys.id}
              items={props?.subNavigationItemsCollection?.items ?? []}
              {...props}
            />
          ),
        });
        break;
      }

      case 'BlockHero': {
        blocksWithBreaks.push({
          data: props,
          component: props.useLatestPost ? (
            <LatestPostHero key={sys.id} backgroundColor={props.backgroundColor} />
          ) : (
            <Hero
              key={sys.id}
              {...props}
              fitWindow={scrollSnap || props.fitWindow}
              fallbackDate={slugContext.postedDate ?? slugContext.publishedAt}
              shareable={slugContext.isShareable}
              topics={slugContext.topics}
            />
          ),
        });
        break;
      }

      case 'LatestPosts': {
        blocksWithBreaks.push({
          data: props,
          component: <LatestPostBlock {...props} key={sys.id} />,
        });
        break;
      }

      case 'MultiVideoBlock': {
        blocksWithBreaks.push({
          data: props,
          component: <MultiVideoBlock key={sys.id} id={sys.id} />,
        });
        break;
      }

      case 'Block': {
        blocksWithBreaks.push({
          data: props,
          component: (
            <Block
              key={sys.id}
              {...props}
              isPreviousSameBackgroundColor={props.backgroundColor === prevBackgroundColor}
              isNextSameBackgroundColor={props.backgroundColor === nextBackgroundColor}
              fullHeight={scrollSnap || props.fullHeight}
              denseLayout={props.denseLayout ?? denseLayout}
            />
          ),
        });
        break;
      }

      case 'SplitBlock': {
        blocksWithBreaks.push({
          data: props,
          component: <SplitBlock key={sys.id} {...props} />,
        });
        break;
      }

      case 'MultiValuePropBlock': {
        blocksWithBreaks.push({
          data: props,
          component: <MultiValuePropBlock key={sys.id} {...props} />,
        });
        break;
      }

      case 'BlockTabs': {
        blocksWithBreaks.push({
          data: props,
          component: <BlockTabs key={sys.id} {...props} />,
        });
        break;
      }

      case 'ImmersiveScrollBlock': {
        blocksWithBreaks.push({
          data: props,
          component: <ImmersiveScrollBlock key={sys.id} {...props} />,
        });
        break;
      }

      default: {
        const customBlocks = getCustomComponents();

        if (__typename in customBlocks) {
          const CustomComponent = customBlocks[__typename]!;

          blocksWithBreaks.push({
            data: props,
            component: <CustomComponent key={sys.id} id={sys.id} />,
          });
        } else {
          blocksWithBreaks.push({
            // Putting in a dummy value for iteration safety.
            data: {
              __typename: 'Break',
              sys: { id: '__generated_error' },
              brandType: 'Straight',
            },
            component: (
              <Fragment key={sys.id}>
                {!Config.isCompilationModeProd &&
                  `Dev error: Unknown component type ${__typename} with id ${sys.id}`}
              </Fragment>
            ),
          });
        }
      }
    }

    // Auto-insert breaks
    if (!isLastBlock) {
      const skipBreak =
        __typename === 'Break' ||
        nextBlock?.__typename === 'Break' ||
        __typename === 'SubNavigation' ||
        nextBlock?.__typename === 'SubNavigation' ||
        isCustomComponent(__typename) ||
        isCustomComponent(nextBlock?.__typename) ||
        (blockHasBackgroundMedia && nextBlockHasBackgroundMedia) ||
        (blockEffectiveColor === nextEffectiveColor &&
          !blockHasBackgroundMedia &&
          !nextBlockHasBackgroundMedia) ||
        breakTemplates.length <= 0;

      // Auto-insert break if needed
      if (!skipBreak) {
        const configHeroBreakTemplate =
          alternateBrandPropOverride?.heroBreakTemplate ?? Config.theme?.heroBreakTemplate;

        let heroBreakTemplate: Template;

        if (configHeroBreakTemplate === Template.None) {
          heroBreakTemplate = Template.None;
        } else if (configHeroBreakTemplate === Template.Straight) {
          heroBreakTemplate = Template.Straight;
        } else {
          heroBreakTemplate = Template.Skirt3;
        }

        const template =
          __typename === 'BlockHero' ? heroBreakTemplate : breakTemplates[templateIndex]!;

        const brandTopColor = blockEffectiveColor;
        const brandBottomColor = nextEffectiveColor;

        const isOverlaid = false;

        blocksWithBreaks.push({
          data: {
            __typename: 'Break',
            sys: { id: '__generated' },
            brandType: template,
            brandTopColor,
            brandBottomColor,
            isOverlaid,
          },
          component: (
            <BreakSDS
              key={`break ${idx}`}
              template={template}
              topColor={brandTopColor}
              bottomColor={brandBottomColor}
              isOverlaid={isOverlaid}
            />
          ),
        });

        // Rotate template to use, unless this block is a Hero as that always uses a specific template
        if (__typename !== 'BlockHero') {
          templateIndex = (templateIndex + 1) % breakTemplates.length;
        }
      }
    }
  });

  // =================================================================================================
  // Add break spacers where 'Break' component overlays blocks/components
  // =================================================================================================
  const blocksToRender = blocksWithBreaks.map((block, idx) => {
    const { component } = block;
    const prevBlock = blocksWithBreaks?.[idx - 1];
    const nextBlock = blocksWithBreaks?.[idx + 1];

    const breakSpacerProps: {
      preChildren?: ReactNode;
      postChildren?: ReactNode;
    } = {};

    // If previous and/or next block is a 'Break' that overlays the block/component, inject a
    // 'BreakSpacer' component as pre/post children to the block/component, which will counter
    // the amount that the 'Break' overlays so that any content is not obscured and background
    // media continues behind the break
    // Do not add spacers for straight breaks as they will not obscure content
    if (prevBlock && prevBlock.data.__typename === 'Break') {
      // Use the newer 'brandType' property if it exists, otherwise use the older 'type' property
      const prevBreakType = prevBlock?.data.brandType ?? prevBlock?.data.type;

      if (prevBreakType !== Template.Straight && prevBreakType !== 'None') {
        const prevTopColor = prevBlock.data.brandTopColor ?? prevBlock.data.topColor;
        const prevBottomColor = prevBlock.data.brandBottomColor ?? prevBlock.data.bottomColor;

        const topBreakOverlayType = getBreakOverlayType({
          template: prevBreakType as Template,
          isTopTransparent: !prevTopColor || prevTopColor === 'Transparent',
          isBottomTransparent: !prevBottomColor || prevBottomColor === 'Transparent',
          isOverlaid: prevBlock.data.isOverlaid,
        });

        breakSpacerProps.preChildren = <BreakSpacerSDS type={topBreakOverlayType} location="top" />;
      }
    }

    if (nextBlock && nextBlock.data.__typename === 'Break') {
      // Use the newer 'brandType' property if it exists, otherwise use the older 'type' property
      const nextBreakType = nextBlock?.data.brandType ?? nextBlock?.data.type;

      if (nextBreakType !== Template.Straight && nextBreakType !== 'None') {
        const nextTopColor = nextBlock.data.brandTopColor ?? nextBlock.data.topColor;
        const nextBottomColor = nextBlock.data.brandBottomColor ?? nextBlock.data.bottomColor;

        const bottomBreakOverlayType = getBreakOverlayType({
          template: nextBreakType as Template,
          isTopTransparent: !nextTopColor || nextTopColor === 'Transparent',
          isBottomTransparent: !nextBottomColor || nextBottomColor === 'Transparent',
          isOverlaid: nextBlock.data.isOverlaid,
        });

        breakSpacerProps.postChildren = (
          <BreakSpacerSDS type={bottomBreakOverlayType} location="bottom" />
        );
      }
    }

    // The last block should have a post child to add extra spacing.
    if (!nextBlock && !breakSpacerProps.postChildren) {
      breakSpacerProps.postChildren = (
        <BreakSpacerSDS type={BreakOverlayType.TOP_HALF} location="bottom" />
      );
    }

    return cloneElement(component, breakSpacerProps);
  });

  // =================================================================================================
  // Page Bottom Fixed Elements
  // =================================================================================================
  // This wrapper ensures that the footnote and AI localization banner are positioned correctly
  // at the bottom of the page main content.
  const pageBottomFixedElements =
    footnoteBlock || aiLocalizationBanner ? (
      <div className={pageBottomFixedElementsCss}>
        {footnoteBlock}
        {aiLocalizationBanner}
      </div>
    ) : undefined;

  // =================================================================================================
  // Content
  // =================================================================================================

  // New background media

  const {
    media: { imageSource, videoSource },
    mobileMedia: { imageSource: mobileImageSource, videoSource: mobileVideoSource },
    thumbnailSource,
  } = useParseMediaContainer(backgroundMediaV2);

  let imgSrcs: ImageSources | undefined;

  if (backgroundMediaV2?.__typename === 'Image') {
    const { desktopSettings, mobileSettings } = getImageSourceSettings({
      desktopHeight: backgroundMediaV2.media?.height ?? 0,
      mobileHeight: backgroundMediaV2.mobileMedia?.height ?? 0,
      enableHighDpi: backgroundMediaV2.enableHighDpi,
      quality: backgroundMediaV2.quality,
    });

    imgSrcs = combineImageSources({
      desktop: getImageSources(imageSource, desktopSettings),
      mobile: getImageSources(mobileImageSource, mobileSettings),
    });
  }

  // set Page to ltr unless title points us to rtl
  const dir = direction(title);

  const content = (
    <>
      <DefinitionProvider>
        <PictureContext.Provider value={pictureContextNoLazy}>
          {blocksToRender.slice(0, firstHeroOrBlockIndex + 1)}
        </PictureContext.Provider>
        <PictureContext.Provider value={pictureContextLazy}>
          {blocksToRender.slice(firstHeroOrBlockIndex + 1)}
        </PictureContext.Provider>
        <DefinitionOverlay />
      </DefinitionProvider>
      {pageBottomFixedElements}
    </>
  );

  let banner: ReactNode = null;
  let customBanner: ReactNode = null;

  if (props?.banner?.__typename === 'Banner') {
    const { contentfulDescriptionDataset } = getContentfulInspectorProps<BannerType>({
      entryId: props.banner.sys.id,
      fieldIds: ['contentfulDescription'],
    });

    banner = (
      <Banner
        className={pageStickyCss}
        contentClassName={bannerContentContainerCss}
        motifScheme={bannerMotifScheme}
        backgroundColor={props.banner?.backgroundColor as BackgroundColor}
        dataset={contentfulDescriptionDataset}
      >
        {renderRichTextWithElements(props.banner?.content as RichText | string)}
      </Banner>
    );
  } else if (props?.banner?.__typename) {
    const customBlocks = getCustomComponents();

    if (customBlocks[props.banner.__typename]) {
      const CustomComponent = customBlocks[props.banner.__typename]!;
      customBanner = <CustomComponent id={props.banner.sys.id} />;
    }
  }

  // =================================================================================================
  // Render
  // =================================================================================================
  return (
    <div
      data-testid="page"
      ref={pageRef}
      className={cx(
        pageContainerCss,
        pageMotifScheme ?? getBackgroundClassName(pageBackgroundColor),
        {
          [pageContainerWithSideNavCss]: hasSideNav,
        },
        minHeightCss,
        'mwp-page'
      )}
      dir={dir}
      data-test-feature={testFeature}
      style={
        {
          [pageStickyHeightCssVar]: `${stickyHeightRef.current}px`,
        } as CSSProperties
      }
    >
      <Canonical localizedIn={props.localizedIn} />
      <AlternateLang localizedIn={props.localizedIn} />
      {!!metas && <Metas {...metas} />}
      {!!metas?.schemasCollection?.items && <WebSchemas schemas={metas.schemasCollection.items} />}

      {banner ? (
        <div className={pageStickyCss} ref={stickyContainerRef}>
          {banner}
        </div>
      ) : null}
      <HeaderSDS title={title} />
      {/* By including the HlsInitializer wrapper here, we are adding Hls playback support to the Page and any of its children */}
      <HlsInitializer>
        <PageSDS
          motifScheme={pageMotifScheme}
          backgroundColor={pageBackgroundColor}
          backgroundImageSources={imgSrcs}
          backgroundMediaStyle={backgroundMediaStyle}
          backgroundVideoSource={videoSource}
          gutterMatchesPageBackground={props.stretchPageBackgroundColor}
          mobileBackgroundVideoSource={mobileVideoSource}
          backgroundPosterSource={thumbnailSource}
          className={cx({ [pageCss]: !hasSubNav, [pageWithSubNavCss]: hasSubNav })}
          scrollSnap={scrollSnap}
        >
          {content}
          {customBanner}
          {isMobile && mobileCta && (
            <PersistentCta
              mobileCta={mobileCta}
              shouldScrollToShow={
                featureFlags[Feature.ENABLE_PERSISTENT_CTA_SCROLL_VISIBILITY] === 'true'
              }
            />
          )}
          <PageBottomStickyPortal className={stickyBottomCss} />
        </PageSDS>
      </HlsInitializer>
      <ActiveEventCountTracker />
    </div>
  );
};

Page.displayName = 'Page';
